// Compact join-the-hackathon card. Used inside the Roadmap drawer and the
// home Learn-Everything feed so the CTA appears in both surfaces the user
// asked for.
import { Link } from "react-router-dom";
import { Trophy, ArrowRight } from "lucide-react";
import { useHackathon, formatUsd } from "@/hooks/useHackathon";

type Props = {
  variant?: "compact" | "feed";
  onNavigate?: () => void;
};

export default function HackathonCTA({ variant = "feed", onNavigate }: Props) {
  const { pot } = useHackathon({ scoreboardLimit: 1 });

  const potLabel = pot ? formatUsd(pot.pot_cents) : "$10,000";
  const unlocked = pot?.unlocked ?? false;
  const paid = pot?.paid_users ?? 0;
  const threshold = pot?.unlock_threshold_users ?? 1000;

  if (variant === "compact") {
    return (
      <Link
        to="/hackathon"
        onClick={onNavigate}
        className="block rounded-lg border border-primary/40 bg-gradient-to-br from-primary/10 via-card to-card p-3 hover:border-primary transition-colors"
      >
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono font-semibold text-foreground">
              Join the Hackathon
            </div>
            <div className="text-[10px] font-mono text-muted-foreground">
              Pot: <span className="text-primary">{potLabel}</span>
              {!unlocked && (
                <>
                  {" "}· unlocks at {threshold.toLocaleString()} paid users
                </>
              )}
            </div>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
      </Link>
    );
  }

  return (
    <section className="container mx-auto px-4 py-6">
      <Link
        to="/hackathon"
        className="group relative block overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/15 via-card to-card p-5 sm:p-6 hover:border-primary transition-colors"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/15 border border-primary/40 shrink-0">
            <Trophy className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-wider text-primary mb-1">
              Hackathon · Always-on
            </div>
            <h3 className="text-xl sm:text-2xl font-mono font-extrabold text-foreground">
              Build the most. Win the pot.
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Monthly cash prizes for top hackers · annual{" "}
              <span className="text-foreground/80">Hacker of the Year</span>{" "}
              award. Pot grows with every paid user.
            </p>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-3xl font-mono font-extrabold text-primary tabular-nums">
                {potLabel}
              </span>
              <span className="text-[11px] font-mono text-muted-foreground">
                current pot · {paid.toLocaleString()} paid users
              </span>
            </div>
            {!unlocked && (
              <p className="text-[10px] font-mono text-muted-foreground/80 mt-1">
                Pot is sponsored at $10,000 once HackerLab reaches{" "}
                {threshold.toLocaleString()} paid users.
              </p>
            )}
          </div>
          <div className="inline-flex items-center gap-2 self-start sm:self-center rounded-full border border-primary bg-primary text-primary-foreground px-4 py-2 text-xs font-mono font-semibold group-hover:bg-primary/90 transition-colors">
            Join Hackathon <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </Link>
    </section>
  );
}